import { EQUIPMENT_MAX_LEVEL } from './catalog';

/**
 * What a level is worth, in exact rational arithmetic.
 *
 * ## The rule
 *
 * Each level multiplies the value the PREVIOUS level had:
 *
 *     Nv.2 = Nv.1 x 1,10      Nv.7  = Nv.6 x 1,60
 *     Nv.3 = Nv.2 x 1,20      Nv.8  = Nv.7 x 1,70
 *     Nv.4 = Nv.3 x 1,30      Nv.9  = Nv.8 x 1,80
 *     Nv.5 = Nv.4 x 1,40      Nv.10 = Nv.9 x 1,90
 *     Nv.6 = Nv.5 x 1,50
 *
 * which compounds to 33,52212864 at level 10.
 *
 * ## Why the multipliers are stored, not multiplied
 *
 * The effective value is ALWAYS derived from the instance's immutable level-1
 * roll: `effective = floor(base x cumulative(level))`. It is never computed by
 * multiplying the previous level's effective value, and that distinction is the
 * whole reason this file exists. Chaining nine roundings drifts - a roll of 137
 * taken up step by step with integer rounding lands on a different number from
 * the same roll multiplied once by 33,52212864 - and two clients that drifted
 * differently would disagree about a player's damage.
 *
 * The cumulative multipliers are exact at eight decimal places, so they are
 * stored as integer numerators over `10^8` and the arithmetic is `bigint`
 * throughout. Nothing here is ever a float.
 *
 * ## The one rounding rule
 *
 * `floor`, once, at the end. Stated here and applied nowhere else:
 *
 * - it is the same direction the Gold and XP fractions already round, so a
 *   player can never gain a unit that was not earned;
 * - it happens after the single multiplication, so the error is bounded by one
 *   unit of the attribute's own sub-unit - a thousandth of a damage point, a
 *   millionth of an attack per second - rather than accumulating per level;
 * - the roll bands are large enough (hundreds to tens of thousands) that the
 *   floor is never the difference between an attribute working and not.
 */

/** `cumulative(level)` is stored as this numerator over 10^8. */
export const LEVEL_MULTIPLIER_DENOMINATOR = 100_000_000n;

/**
 * Cumulative multiplier per level, exact.
 *
 * Index 0 is level 1. Every entry is the product of the per-level steps above,
 * written out rather than computed, so a typo is a failing test rather than a
 * quiet rebalance - `levels.test.ts` recomputes them from the steps and
 * compares.
 */
export const LEVEL_MULTIPLIER_NUMERATORS: readonly bigint[] = Object.freeze([
  100_000_000n, // 1
  110_000_000n, // 1,1
  132_000_000n, // 1,32
  171_600_000n, // 1,716
  240_240_000n, // 2,4024
  360_360_000n, // 3,6036
  576_576_000n, // 5,76576
  980_179_200n, // 9,801792
  1_764_322_560n, // 17,6432256
  3_352_212_864n, // 33,52212864
]);

/**
 * The per-level STEP, in basis points: what level L multiplies level L-1 by.
 *
 * Index 0 is the step INTO level 2. Present so the compounding rule is data
 * rather than a comment, and so the cumulative table above can be proved.
 */
export const LEVEL_STEP_BPS: readonly number[] = Object.freeze([
  11_000, // -> 2
  12_000, // -> 3
  13_000, // -> 4
  14_000, // -> 5
  15_000, // -> 6
  16_000, // -> 7
  17_000, // -> 8
  18_000, // -> 9
  19_000, // -> 10
]);

export class InvalidEquipmentLevelError extends RangeError {
  constructor(level: number) {
    super(`equipment level must be an integer in [1, ${EQUIPMENT_MAX_LEVEL}], got ${level}`);
    this.name = 'InvalidEquipmentLevelError';
  }
}

export function assertEquipmentLevel(level: number): void {
  if (!Number.isSafeInteger(level) || level < 1 || level > EQUIPMENT_MAX_LEVEL) {
    throw new InvalidEquipmentLevelError(level);
  }
}

/** The cumulative numerator for a level, over `LEVEL_MULTIPLIER_DENOMINATOR`. */
export function levelMultiplierNumerator(level: number): bigint {
  assertEquipmentLevel(level);
  const numerator = LEVEL_MULTIPLIER_NUMERATORS[level - 1];
  if (numerator === undefined) {
    throw new InvalidEquipmentLevelError(level);
  }
  return numerator;
}

/**
 * The effective value of one attribute at one level.
 *
 * `floor(base x cumulative(level))`, in the attribute's own integer unit,
 * derived from the IMMUTABLE base every single time. There is no other way to
 * compute an effective value anywhere in the project.
 */
export function effectiveAttributeValue(baseValue: bigint, level: number): bigint {
  if (baseValue < 0n) {
    throw new RangeError('an equipment roll is never negative');
  }
  return (baseValue * levelMultiplierNumerator(level)) / LEVEL_MULTIPLIER_DENOMINATOR;
}

/**
 * The multiplier as a readable decimal string, for the interface and the docs.
 *
 * Produced from the exact numerator rather than from a float, so what the panel
 * prints is the number the arithmetic used.
 */
export function levelMultiplierText(level: number): string {
  const numerator = levelMultiplierNumerator(level);
  const whole = numerator / LEVEL_MULTIPLIER_DENOMINATOR;
  const fraction = (numerator % LEVEL_MULTIPLIER_DENOMINATOR).toString(10).padStart(8, '0');
  const trimmed = fraction.replace(/0+$/, '');
  return trimmed.length === 0 ? whole.toString(10) : `${whole},${trimmed}`;
}
